import { HTMLAttributes, MouseEvent } from "react";
import { useFormContext, useWatch } from "react-hook-form";
import classnames from "classnames";
import Chip from "@mui/material/Chip";
import { AutocompleteRenderValueGetItemProps } from "@mui/material/Autocomplete";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus } from "@fortawesome/free-solid-svg-icons";
import { UFDoraSelectMultiple } from "dora-shared/frontend";
import CreateEditShipmentData from "../CreateEditShipmentData";
import "./ProductsChipSelect.scss";

type ProductOption = { id: string; label: string };

type ProductsChipSelectProps = {
  options: ProductOption[];
  selectableOptions: ProductOption[];
  placeholder?: string;
  onAddLine: (productId: string) => void;
  lineCountByProductId: Record<string, number>;
};

// Products select where clicking a dropdown option adds the product (if not
// already selected) and one invoice line, every time it is clicked. Products are
// removed from the chips in the input; that leaves their invoice lines alone.
const ProductsChipSelect = ({
  options,
  selectableOptions,
  placeholder,
  onAddLine,
  lineCountByProductId,
}: ProductsChipSelectProps) => {
  const { control, getValues, setValue } =
    useFormContext<CreateEditShipmentData>();
  // Subscribe so the chips re-render when products change
  useWatch({ control, name: "products" });

  const currentProducts = (): ProductOption[] =>
    ((getValues("products") || []) as unknown as ProductOption[]).filter(
      Boolean
    );

  const setProducts = (products: ProductOption[]) =>
    setValue("products", products as any, { shouldDirty: true });

  const onChipClick = (e: MouseEvent, option: ProductOption) => {
    // Replaces MUI's option click, which would toggle the selection
    e.preventDefault();
    const products = currentProducts();
    if (!products.some((p) => p.id === option.id)) {
      setProducts([...products, option]);
    }
    onAddLine(option.id);
  };

  // Product name followed by a gray "(xN)" with how many invoice lines use it
  const renderLabel = (option: ProductOption) => {
    const count = lineCountByProductId[option.id] || 0;
    return (
      <>
        {option.label}
        {count > 0 && (
          <span className="products-chip-select__count">(x{count})</span>
        )}
      </>
    );
  };

  const renderValue = (
    value: ProductOption[],
    getItemProps: AutocompleteRenderValueGetItemProps<true>
  ) =>
    value.map((option, index) => {
      const { key, ...itemProps } = getItemProps({ index });
      return (
        <Chip
          key={key}
          size="small"
          {...itemProps}
          label={renderLabel(option)}
        />
      );
    });

  const renderOption = (
    props: HTMLAttributes<HTMLLIElement>,
    option: ProductOption
  ) => (
    <li
      {...props}
      key={option.id}
      className={classnames(props.className, "products-chip-select__option")}
      onClick={(e) => onChipClick(e, option)}
    >
      <span className="products-chip-select__label">{renderLabel(option)}</span>
      <span className="products-chip-select__add">
        <FontAwesomeIcon icon={faPlus} />
      </span>
    </li>
  );

  return (
    <UFDoraSelectMultiple
      control={control}
      options={options}
      selectableOptions={selectableOptions}
      name={"products"}
      placeholder={placeholder}
      customRenderOption={renderOption}
      customRenderValue={renderValue}
    />
  );
};

export default ProductsChipSelect;
